import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertModal } from '@/components/modal/alert-modal';
import { useDeleteMyInquiry } from '@/services/inquiry/queries';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Calendar, 
  Trash2, 
  Mail, 
  CheckCircle, 
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Inquiry } from '@/services/types';
import AdminResponse from './admin-response';

type InquiryCardProps = {
  inquiry: Inquiry;
  onDelete?: () => void;
};

export default function InquiryCard({ inquiry, onDelete }: InquiryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { mutate: deleteInquiry, isPending } = useDeleteMyInquiry(
    () => {
      toast.success('Inquiry deleted successfully');
      setShowDeleteModal(false);
      onDelete?.();
    },
    (error: any) => {
      toast.error(error.response.data.message || 'Failed to delete inquiry');
    }
  );

  const handleDelete = () => {
    deleteInquiry(inquiry.id);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const isResolved = status === 'resolved';
    return (
      <Badge
        variant='outline'
        className={cn(
          'flex items-center gap-1 border-0 text-xs font-medium',
          isResolved
            ? 'bg-green-50 text-green-700 hover:bg-green-100'
            : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
        )}
      >
        {isResolved ? (
          <CheckCircle className='h-3 w-3' />
        ) : (
          <Clock className='h-3 w-3' />
        )}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <>
      <Card className='hover:shadow-md transition-shadow'>
        <CardHeader className='pb-3'>
          <div className='flex items-start justify-between'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 mb-2'>
                <MessageSquare className='h-4 w-4 text-gray-500' />
                <h3 className='font-semibold text-gray-900 truncate'>
                  {inquiry.subject || 'No Subject'}
                </h3>
              </div>
              
              <div className='flex items-center gap-4 text-sm text-gray-600'>
                <div className='flex items-center gap-1'>
                  <Mail className='h-3 w-3' />
                  <span className='truncate'>{inquiry.email}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <Calendar className='h-3 w-3' />
                  <span>{formatDate(inquiry.createdAt)}</span>
                </div>
              </div>
            </div>
            
            <div className='flex items-center gap-2 ml-4'>
              {getStatusBadge(inquiry.status)}
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setIsExpanded(!isExpanded)}
                className='p-1'
              >
                {isExpanded ? (
                  <ChevronUp className='h-4 w-4' />
                ) : (
                  <ChevronDown className='h-4 w-4' />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className='pt-0 space-y-4'>
            <div>
              <h4 className='font-medium text-gray-900 mb-2'>Message</h4>
              <div className='text-sm text-gray-700 bg-gray-50 p-3 rounded-md whitespace-pre-wrap'>
                {inquiry.message}
              </div>
            </div>

            {/* Admin Response - Only show if inquiry has been resolved */}
            <AdminResponse
              response={inquiry.response}
              resolvedAt={inquiry.resolvedAt}
            />

            <div className='flex justify-end pt-2 border-t'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setShowDeleteModal(true)}
                className='text-red-600 hover:text-red-700 hover:bg-red-50'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Delete
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isPending}
        title='Delete Inquiry'
        description='Are you sure you want to delete this inquiry? This action cannot be undone.'
      />
    </>
  );
}